import React, { useEffect, useState } from "react";

import { marked } from "marked";
import DOMPurify from "dompurify";

import PageContent from "ui/PageContent";
import Panel from "ui/Panel";
import PanelSection from "ui/PanelSection";
import Loading from "ui/Loading";

import moment from "moment";
import { formatDateMonthDayYear } from "utils/format";
import { useAppConfig } from "providers/WithAppConfig";
import groupBy from "lodash/groupBy";
import sortBy from "lodash/sortBy";
import classNames from "classnames";
import { useCurrentUser } from "providers/WithCurrentOrganization";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGithub } from "@fortawesome/free-brands-svg-icons";

marked.use({
  renderer: {
    link(href: string, title: string, text: string) {
      const titleAttr = title ? `title="${title}"` : "";
      return `<a href="${href}" target="_blank" ${titleAttr}>${text}</a>`;
    },
  },
});

type ChangelogChannel = "enterprise-server" | "cloud";

type ChangelogDeployEntry = {
  pr: number;
  summary: string;
  channels: ChangelogChannel[];
  description: string | null;
};

type ChangelogDeploy = {
  deployed_at: string;
  entries: ChangelogDeployEntry[];
};

type Changelog = {
  deployed: ChangelogDeploy[];
};

const Changelog: React.FunctionComponent = () => {
  const { changelog, error } = useChangelog();

  return (
    <PageContent
      title="What's New"
      pageCategory="changelog"
      pageName="changelog"
    >
      <div className="max-w-[1000px]">
        <Panel title="Changelog">
          <PanelSection>
            {changelog ? (
              <ChangelogContent changelog={changelog} />
            ) : (
              <Loading error={!!error} />
            )}
          </PanelSection>
        </Panel>
      </div>
    </PageContent>
  );
};

function useChangelog() {
  const { changelogUrl } = useAppConfig();
  const [changelog, setChangelog] = useState<Changelog | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    fetch(changelogUrl)
      .then((resp) => resp.json())
      .then((result) => setChangelog(result))
      .catch((err) => setError(err));
  }, [changelogUrl]);

  const cloudChangelog = changelog
    ? {
        ...changelog,
        deployed: changelog.deployed
          .map((deploy) => {
            const deployedDate = formatDateMonthDayYear(
              moment(deploy.deployed_at),
            );
            return {
              deployed_at: deployedDate,
              entries: deploy.entries.filter((e) =>
                e.channels.includes("cloud"),
              ),
            };
          })
          .filter(
            (deploy) =>
              deploy.entries.length > 0 &&
              moment(deploy.deployed_at).isAfter(moment("2025-01-01")),
          )
          .reduce<ChangelogDeploy[]>((deploys, deploy) => {
            // collapse multiple deploys on the same day
            const prevDeploy =
              deploys.length === 0 ? undefined : deploys.at(-1);
            if (prevDeploy?.deployed_at === deploy.deployed_at) {
              prevDeploy.entries.push(...deploy.entries);
            } else {
              deploys.push(deploy);
            }

            return deploys;
          }, []),
      }
    : undefined;

  return { changelog: cloudChangelog, error };
}

const ChangelogContent: React.FunctionComponent<{ changelog: Changelog }> = ({
  changelog,
}) => {
  return (
    <div>
      {changelog.deployed
        .slice()
        .reverse()
        .map((deploy) => {
          return <ChangelogDeploy key={deploy.deployed_at} deploy={deploy} />;
        })}
    </div>
  );
};

function ChangelogDeploy({ deploy }: { deploy: ChangelogDeploy }) {
  if (deploy.entries.length === 0) {
    return null;
  }
  const deployId = deploy.deployed_at;

  const entriesByCategory = groupBy(deploy.entries, (entry) => {
    return entry.summary.split(":")[0];
  });
  const groupedEntries = sortBy(
    Object.entries(entriesByCategory).map(([category, entries]) => {
      if (entries.length === 1) {
        // no need to group
        return [category, entries] as const;
      }
      const categoryEntries = entries.map((entry) => {
        return {
          ...entry,
          summary: entry.summary.replace(new RegExp(`^${category}: ?`), ""),
        };
      });

      return [category, categoryEntries] as const;
    }),
    ([category]) => {
      return category;
    },
  );

  return (
    <div className="mx-4 border-b last:border-b-0 border-gray-200 first:mt-4 mt-6">
      <h4 className="mt-1 mb-4 flex items-start justify-between">
        <a
          id={deployId}
          href={`#${deployId}`}
          className="text-3xl text-inherit font-normal leading-none after:content-['_#'] after:invisible hover:after:visible"
        >
          {deploy.deployed_at}
        </a>
      </h4>
      <ul className="flex-1 !ml-6 list-outside">
        {groupedEntries.map(([category, entries]) => {
          if (entries.length === 1) {
            return <ChangelogDeployEntry key={category} entry={entries[0]} />;
          }
          return (
            <GroupedChangelogDeployEntry
              key={category}
              category={category}
              entries={entries}
            />
          );
        })}
      </ul>
    </div>
  );
}

function ChangelogDeployEntry({ entry }: { entry: ChangelogDeployEntry }) {
  const content = getEntryContent(entry);
  return (
    <ChangelogListItem>
      <ChangelogEntrySummary entry={entry} />
      {content && (
        <div className="mt-1" dangerouslySetInnerHTML={{ __html: content }} />
      )}
    </ChangelogListItem>
  );
}

function ChangelogEntrySummary({ entry }: { entry: ChangelogDeployEntry }) {
  const { permittedToReviewChangelog } = useCurrentUser();

  return (
    <div className="flex items-start justify-between group">
      <span className="font-semibold">{entry.summary}</span>
      {permittedToReviewChangelog && <EntryGitHubLink entry={entry} />}
    </div>
  );
}

function EntryGitHubLink({ entry }: { entry: ChangelogDeployEntry }) {
  const url = getPullRequestURL(entry);
  return (
    <span className="text-sm whitespace-nowrap hidden group-hover:block">
      <FontAwesomeIcon icon={faGithub} />{" "}
      <a href={url} target="_blank">
        #{entry.pr}
      </a>
    </span>
  );
}

function getPullRequestURL(entry: ChangelogDeployEntry) {
  return `https://github.com/pganalyze/pganalyze/pull/${entry.pr}`;
}

function ChangelogListItem(props: React.ComponentProps<"li">) {
  const { className, ...rest } = props;
  return (
    <li className={classNames("mb-2 last:mb-4 text-lg", className)} {...rest} />
  );
}

function GroupedChangelogDeployEntry({
  category,
  entries,
}: {
  category: string;
  entries: ChangelogDeployEntry[];
}) {
  return (
    <ChangelogListItem>
      <span className="font-semibold">{category}</span>
      <ul>
        {entries.map((entry, i) => {
          const content = getEntryContent(entry);
          return (
            <li key={i} className="mb-2 first:mt-1">
              <ChangelogEntrySummary entry={entry} />
              {content && (
                <div
                  className="mt-1"
                  dangerouslySetInnerHTML={{ __html: content }}
                />
              )}
            </li>
          );
        })}
      </ul>
    </ChangelogListItem>
  );
}

function getEntryContent(entry: ChangelogDeployEntry) {
  const content = entry.description
    ? marked.parse(entry.description, { async: false })
    : undefined;
  return DOMPurify.sanitize(content);
}

export default Changelog;
